import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Loader2, Pencil, Plus, Repeat, StopCircle } from 'lucide-react';
import { Button } from '@components/Button';
import { ConfirmDialog } from '@components/Dialog';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ActionButton } from '@components/Table';
import { PriorityBadge } from '@components/activity/ActivityBits';
import recurrenceService from '@services/recurrenceService';
import type { Priority } from '@/types/activity';
import type { RecurrenceRule } from '@/types/planning';
import { describeFrequency, describeWeekdays } from '@/utils/recurrence';
import { formatDateBR } from '@/utils/formatters';
import { RecurrenceFormModal, RecurrencePrefill } from './RecurrenceFormModal';

const num = (v: string | null): number | undefined => (v && Number(v) > 0 ? Number(v) : undefined);

/** Lê o pré-preenchimento do "Tornar recorrente" (`/recurrences?new=1&title=...`). */
const prefillFromParams = (p: URLSearchParams): RecurrencePrefill => ({
  title: p.get('title') ?? undefined,
  description: p.get('description') ?? undefined,
  board_id: num(p.get('board_id')),
  board_column_id: num(p.get('board_column_id')),
  priority: (p.get('priority') as Priority | null) ?? undefined,
  start_date: /^\d{4}-\d{2}-\d{2}$/.test(p.get('start_date') ?? '') ? (p.get('start_date') as string) : undefined,
});

/**
 * Atividades recorrentes (moldes). O job do backend gera uma atividade por ocorrência, com
 * `lead_days` de antecedência. Encerrar (DELETE) para de gerar, sem apagar o que já foi gerado.
 */
export const RecurrenceListView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rules, setRules] = useState<RecurrenceRule[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [editing, setEditing] = useState<RecurrenceRule | null>(null);
  const [creating, setCreating] = useState<RecurrencePrefill | null>(null);
  const [ending, setEnding] = useState<RecurrenceRule | null>(null);
  const [endingBusy, setEndingBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await recurrenceService.getAll(includeInactive));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    load();
  }, [load]);

  // "Tornar recorrente" no modal de atividade chega aqui com ?new=1&...
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setCreating(prefillFromParams(searchParams));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleEnd = async () => {
    if (!ending) return;
    setEndingBusy(true);
    try {
      await recurrenceService.end(ending.id);
      setEnding(null);
      load();
    } catch (err) {
      setEnding(null);
      setError(err);
    } finally {
      setEndingBusy(false);
    }
  };

  const closeForm = () => {
    setEditing(null);
    setCreating(null);
  };

  const repetition = (r: RecurrenceRule) => (
    <>
      <p>{describeFrequency(r.frequency, r.interval_count)}</p>
      {r.frequency === 'weekly' && <p className="text-small">{describeWeekdays(r.days_of_week)}</p>}
      {r.frequency === 'monthly' && (
        <p className="text-small">{r.day_of_month > 0 ? `Dia ${r.day_of_month}` : 'Dia do início'}</p>
      )}
      {r.lead_days > 0 && <p className="text-small">{r.lead_days} dia(s) de antecedência</p>}
    </>
  );

  const actions = (r: RecurrenceRule) => (
    <div className="flex items-center gap-1 justify-end">
      <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setEditing(r)} />
      {r.is_active && (
        <ActionButton icon={<StopCircle size={16} />} variant="delete" tooltip="Encerrar" onClick={() => setEnding(r)} />
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Repeat size={20} className="text-slate-500" /> Recorrências
        </h1>
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox-field"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Mostrar encerradas
          </label>
          <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setCreating({})}>
            Nova recorrência
          </Button>
        </div>
      </div>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && rules.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!loading && rules.length === 0 && <p className="text-center text-slate-500 py-8">Nenhuma recorrência cadastrada.</p>}
        {rules.map((r) => (
          <div key={r.id} className={clsx('card p-3 space-y-2', !r.is_active && 'opacity-60')}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-slate-800">{r.title}</p>
                <p className="text-small">{r.board_name}</p>
              </div>
              <PriorityBadge priority={r.priority} />
            </div>
            <div className="text-sm text-slate-700">{repetition(r)}</div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-small">
              <span>
                {formatDateBR(r.start_date)}
                {r.end_date ? ` a ${formatDateBR(r.end_date)}` : ' — sem fim'}
                {r.next_occurrence ? ` · próxima ${formatDateBR(r.next_occurrence)}` : ''}
              </span>
              {r.is_active ? <span className="badge-success">Ativa</span> : <span className="badge-neutral">Encerrada</span>}
            </div>
            {actions(r)}
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Atividade</th>
                <th>Repetição</th>
                <th>Vigência</th>
                <th>Próxima</th>
                <th>Situação</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {loading && rules.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && rules.length === 0 && (
                <tr>
                  <td colSpan={6} className="table-cell text-center py-12 text-slate-500">
                    Nenhuma recorrência cadastrada.
                  </td>
                </tr>
              )}
              {rules.map((r) => (
                <tr key={r.id} className={clsx('table-row', !r.is_active && 'opacity-60')}>
                  <td className="table-cell">
                    <p className="font-medium text-slate-800">{r.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-small">{r.board_name}</span>
                      <PriorityBadge priority={r.priority} />
                    </div>
                  </td>
                  <td className="table-cell">{repetition(r)}</td>
                  <td className="table-cell whitespace-nowrap">
                    {formatDateBR(r.start_date)}
                    {r.end_date ? ` a ${formatDateBR(r.end_date)}` : ' — sem fim'}
                  </td>
                  <td className="table-cell whitespace-nowrap">
                    {r.next_occurrence ? formatDateBR(r.next_occurrence) : <span className="text-slate-400">—</span>}
                    {r.last_generated_date && (
                      <p className="text-small">última gerada {formatDateBR(r.last_generated_date)}</p>
                    )}
                  </td>
                  <td className="table-cell">
                    {r.is_active ? <span className="badge-success">Ativa</span> : <span className="badge-neutral">Encerrada</span>}
                  </td>
                  <td className="table-cell">{actions(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(editing || creating) && (
        <RecurrenceFormModal
          rule={editing}
          prefill={creating ?? undefined}
          onClose={closeForm}
          onSaved={() => {
            closeForm();
            load();
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        isOpen={!!ending}
        onClose={() => setEnding(null)}
        onConfirm={handleEnd}
        title="Encerrar recorrência"
        message={`Encerrar "${ending?.title ?? ''}"? Novas ocorrências deixam de ser geradas; as atividades já criadas continuam.`}
        confirmText="Encerrar"
        severity="danger"
        loading={endingBusy}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
